import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import authApi, { CommunityScreenerResponse } from '../../services/authApi';
import PageGate from '../PageGate';
import './tool-page.css';
import './CoursesPage.css';
import './ScreenersPage.css';
import './ScreenerDetailPage.css';

const ScreenerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [screener, setScreener] = useState<CommunityScreenerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);

  useEffect(() => {
    if (!id) return;
    authApi.getScreenerById(Number(id))
      .then((r) => setScreener(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const screenerUrl = screener?.chartinkScreenerId
    ? `https://chartink.com/screener/${screener.chartinkScreenerId}`
    : null;

  return (
    <PageGate requirePremium>
      <div className="tool-page sd-page">

        {/* Back */}
        <button className="sd-back" onClick={() => navigate(-1)}>
          ← Back
        </button>

        {loading ? (
          <div className="sd-skeleton-wrap">
            <div className="course-skeleton" style={{ height: 180 }} />
            <div className="course-skeleton" style={{ height: 120 }} />
            <div className="course-skeleton" style={{ height: 80 }} />
          </div>
        ) : notFound || !screener ? (
          <div className="sd-not-found">
            <div className="sd-nf-icon">🔍</div>
            <h2>Screener not found</h2>
            <p>This screener may have been removed or doesn't exist.</p>
            <button className="gate-btn gate-btn--primary" onClick={() => navigate('/screeners')}>
              Browse Screeners
            </button>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="sd-hero">
              <div className="sd-hero-top">
                <div className="sd-badges">
                  <span className={`pill ${screener.isFree ? 'pill-free' : 'pill-pro'}`}>
                    {screener.isFree ? 'Free' : 'Pro'}
                  </span>
                  <span className="sd-tf-badge">
                    <TfIcon /> {screener.timeframe}
                  </span>
                </div>
                <h1 className="sd-title">{screener.name}</h1>
                <div className="sd-tags">
                  {screener.tags.map((t) => (
                    <span key={t} className="pill pill-tag">{t.trim()}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="sd-body">

              {/* Description */}
              <section className="sd-section">
                <h2 className="sd-section-title">About this Screener</h2>
                <p className="sd-desc">{screener.description}</p>
              </section>

              {/* Screener link */}
              <section className="sd-section sd-section--link">
                {screenerUrl ? (
                  <>
                    <h2 className="sd-section-title">Open Screener</h2>
                    <p className="sd-link-hint">
                      Opens the live screener in a new tab. Results update in real time during market hours.
                    </p>
                    <a
                      href={screenerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sd-open-btn"
                    >
                      Open Screener <ExtIcon />
                    </a>
                  </>
                ) : (
                  <div className="sd-no-link">
                    <span className="sd-no-link-icon">🔧</span>
                    <div>
                      <strong>Screener link coming soon</strong>
                      <p>We're adding the live link for this screener. Check back shortly.</p>
                    </div>
                  </div>
                )}
              </section>

              {/* Usage tips */}
              <section className="sd-section">
                <h2 className="sd-section-title">How to use</h2>
                <ul className="sd-tips">
                  <li>{getTimingTip(screener.timeframe)}</li>
                  <li>Cross-check with your chart before acting — screeners are filters, not signals.</li>
                  <li>Use this on a <strong>{screener.timeframe}</strong> timeframe for best results.</li>
                </ul>
              </section>

              {/* Backtesting Results */}
              {screener.backtestSummary && (
                <section className="sd-section">
                  <h2 className="sd-section-title">Backtesting Results</h2>
                  <div className="sd-bt-grid">
                    {screener.backtestSummary.winRate != null && (
                      <div className="sd-bt-stat">
                        <span className="sd-bt-val sd-bt-val--green">{screener.backtestSummary.winRate}%</span>
                        <span className="sd-bt-label">Win Rate</span>
                      </div>
                    )}
                    {screener.backtestSummary.totalTrades != null && (
                      <div className="sd-bt-stat">
                        <span className="sd-bt-val">{screener.backtestSummary.totalTrades}</span>
                        <span className="sd-bt-label">Total Trades</span>
                      </div>
                    )}
                    {screener.backtestSummary.avgReturn != null && (
                      <div className="sd-bt-stat">
                        <span className={`sd-bt-val ${screener.backtestSummary.avgReturn >= 0 ? 'sd-bt-val--green' : 'sd-bt-val--red'}`}>
                          {screener.backtestSummary.avgReturn >= 0 ? '+' : ''}{screener.backtestSummary.avgReturn}%
                        </span>
                        <span className="sd-bt-label">Avg Return</span>
                      </div>
                    )}
                    {screener.backtestSummary.maxDrawdown != null && (
                      <div className="sd-bt-stat">
                        <span className="sd-bt-val sd-bt-val--red">{screener.backtestSummary.maxDrawdown}%</span>
                        <span className="sd-bt-label">Max Drawdown</span>
                      </div>
                    )}
                  </div>
                  {screener.backtestSummary.period && (
                    <p className="sd-bt-period">Tested period: {screener.backtestSummary.period}</p>
                  )}
                  {screener.backtestFileUrl && (
                    <a href={screener.backtestFileUrl} target="_blank" rel="noopener noreferrer" className="sd-download-btn">
                      <DownloadIcon /> Download Full Report
                    </a>
                  )}
                  <p className="sd-bt-disclaimer">Past results are for educational reference only and do not guarantee future performance.</p>
                </section>
              )}

              {/* Pine Script */}
              {screener.pineScript && (
                <section className="sd-section">
                  <div className="sd-code-header">
                    <h2 className="sd-section-title" style={{ margin: 0 }}>Pine Script</h2>
                    <CopyButton text={screener.pineScript} label="Copy Script" />
                  </div>
                  <p className="sd-link-hint">Paste this into TradingView Pine Editor → Add to Chart.</p>
                  <pre className="sd-code-block">{screener.pineScript}</pre>
                </section>
              )}

              {/* Screener Prompt */}
              {screener.screenerPrompt && (
                <section className="sd-section">
                  <div className="sd-code-header">
                    <h2 className="sd-section-title" style={{ margin: 0 }}>Prompt</h2>
                    <CopyButton text={screener.screenerPrompt} label="Copy Prompt" />
                  </div>
                  {screener.screenerPromptDesc && (
                    <p className="sd-link-hint">{screener.screenerPromptDesc}</p>
                  )}
                  <div className={`sd-prompt-block ${promptExpanded ? '' : 'sd-prompt-block--collapsed'}`}>
                    {screener.screenerPrompt}
                  </div>
                  <button className="sd-prompt-toggle" onClick={() => setPromptExpanded((p) => !p)}>
                    {promptExpanded ? '▲ Show less' : '▼ Show more'}
                  </button>
                </section>
              )}

              {/* Related Learning */}
              {screener.relatedTutorials && screener.relatedTutorials.length > 0 && (
                <section className="sd-section">
                  <h2 className="sd-section-title">Related Learning</h2>
                  <p className="sd-link-hint" style={{ marginBottom: 14 }}>
                    These modules cover the concepts and setup behind this screener.
                  </p>
                  <div className="sd-tutorial-list">
                    {screener.relatedTutorials.map((t) => (
                      <button
                        key={t.id}
                        className="sd-tutorial-card"
                        onClick={() => navigate(`/courses/${t.id}`)}
                      >
                        <span className="sd-tut-badge">{t.badge}</span>
                        <div className="sd-tut-info">
                          <span className="sd-tut-title">{t.title}</span>
                          {t.description && <span className="sd-tut-desc">{t.description}</span>}
                        </div>
                        <span className="sd-tut-cta">
                          <PlayIcon /> Watch
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

            </div>
          </>
        )}
      </div>
    </PageGate>
  );
};

export default ScreenerDetailPage;

function getTimingTip(timeframe: string): string {
  const tf = timeframe.toLowerCase();
  const isIntraday = ['1min', '2min', '3min', '5min', '10min', '15min', '20min', '30min',
    '1m', '5m', '10m', '15m', '30m', 'intraday', 'scalp'].some((t) => tf.includes(t));
  const isSwing = ['daily', 'day', 'weekly', 'week', 'monthly', 'eod'].some((t) => tf.includes(t));

  if (isIntraday)
    return 'Run this screener while the market is live — results are most accurate when prices are actively moving.';
  if (isSwing)
    return 'Best reviewed after the session ends so the candle is fully formed and the data is final.';
  return 'Open the screener as per your trading method — results may vary depending on when you scan during the session.';
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button className="sd-copy-btn" onClick={copy}>
      {copied ? <CheckIcon /> : <CopyIcon />}
      {copied ? 'Copied!' : label}
    </button>
  );
}
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11" style={{ flexShrink: 0 }}>
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  );
}
function TfIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}
function ExtIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}
